import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import httpService from '../../services/httpService';
import { HubConnectionBuilder } from '@microsoft/signalr/dist/esm/HubConnectionBuilder';
import { HttpTransportType, LogLevel } from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';

const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'mp4', 'ppt', 'pptx'];

type InitiateMultipartResponse = {
  uploadId: string;
  fileKey: string;
  resourceId?: string;
  isGuardDutyEnabled?: boolean;
};

type ScanInfoMessage = {
  fileName?: string;
  isFine?: boolean;
};

const HelpDocumentsSection = () => {
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [canUpload, setCanUpload] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<string[]>([]);
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const scanResults = useRef<Map<string, boolean>>(new Map());
  const scanWaiters = useRef<Map<string, (isFine: boolean) => void>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);

  const showNotification = useCallback((severity: 'success' | 'error' | 'warn', detail: string) => {
    toast.current?.show({
      severity,
      summary: severity === 'success' ? 'Success' : severity === 'warn' ? 'Warning' : 'Error',
      detail,
      life: 3000,
    });
  }, []);

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error) {
      return error.message;
    }

    return fallback;
  };

  const getResponseErrorMessage = (error: unknown, fallback: string) => {
    const axiosError = error as AxiosError<{ message?: string } | string>;
    const responseData = axiosError.response?.data;

    if (typeof responseData === 'string') {
      return responseData;
    }

    return responseData?.message || getErrorMessage(error, fallback);
  };

  const fetchDocumentTypes = useCallback(async () => {
    try {
      const response = await httpService.get<string[]>(
        `${ApiEndpoints.BASE_URL}/Resource/GetResourceDocumentTypes`
      );

      setDocumentTypes(response.data);
    } catch (error: unknown) {
      showNotification(
        'error',
        `Error fetching document types: ${getErrorMessage(
          error,
          'Unable to fetch document types.'
        )}`
      );
    }
  }, [showNotification]);

  useEffect(() => {
    fetchDocumentTypes();
  }, [fetchDocumentTypes]);

  // AWS GuardDuty scan status is pushed over SignalR, same hub used for provider file uploads
  useEffect(() => {
    const newConnection = new HubConnectionBuilder()
      .withUrl(ApiEndpoints.fileProcessingHub, {
        transport: HttpTransportType.ServerSentEvents,
      })
      .configureLogging(LogLevel.Information)
      .build();

    newConnection
      .start()
      .then(() => setConnection(newConnection))
      .catch((err) =>
        console.error('Error establishing SignalR connection:', err)
      );

    return () => {
      if (newConnection) {
        newConnection.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (connection) {
      const handleScanInfo = (message: ScanInfoMessage) => {
        if (!message?.fileName) {
          return;
        }

        const isFine = message.isFine === true;
        const waiter = scanWaiters.current.get(message.fileName);

        if (waiter) {
          scanWaiters.current.delete(message.fileName);
          waiter(isFine);
          return;
        }

        scanResults.current.set(message.fileName, isFine);
      };

      connection.on('ScanInfo', handleScanInfo);

      return () => {
        connection.off('ScanInfo', handleScanInfo);
      };
    }
  }, [connection]);

  const waitForScanResult = (fileName: string): Promise<boolean> => {
    const existingResult = scanResults.current.get(fileName);

    if (existingResult !== undefined) {
      scanResults.current.delete(fileName);
      return Promise.resolve(existingResult);
    }

    return new Promise((resolve) => {
      scanWaiters.current.set(fileName, resolve);
    });
  };

  const fileFormatHint = useMemo(
    () =>
      `Accepted formats: ${allowedExtensions
        .map((ext) => `.${ext}`)
        .join(', ')}`,
    []
  );

  const validateFile = (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return allowedExtensions.includes(extension);
  };

  const handleUpload = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedDocumentType || !selectedFile) {
      showNotification(
        'warn',
        'Please select both a document type and a file before uploading.'
      );

      return;
    }

    const file = selectedFile;

    if (!validateFile(file)) {
      showNotification(
        'warn',
        'Selected file format is not supported. Please upload a supported file type.'
      );

      return;
    }

    setIsLoading(true);
    setCanUpload(true);
    setUploadProgress(0);

    try {
      if (file.size > 1024 * 1024 * 1024) {
        showNotification(
          'warn',
          'File size should be less than 1 GB.'
        );

        setCanUpload(false);
        setIsLoading(false);

        return;
      }

      const chunkSize = 5 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / chunkSize);
      const tags: string[] = [];

      const initiateResult =
        await httpService.post<InitiateMultipartResponse>(
          ApiEndpoints.initiateResourceMultipartUploadUrl,
          {
            documentType: selectedDocumentType,
            fileName: file.name,
            connectionId: connection?.connectionId,
            uploadType: Constants.UPLOAD_TYPE_RESOURCE,
          }
        );

      const uploadId = initiateResult.data.uploadId;
      const fileKey = initiateResult.data.fileKey;
      const resourceId = initiateResult.data.resourceId;

      for (let i = 0; i < totalChunks; i += 1) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();

        formData.append('file', chunk, file.name);
        formData.append('chunkIndex', i.toString());
        formData.append('uploadId', uploadId);
        formData.append('fileKey', fileKey);
        formData.append(
          'isLastChunk',
          (i === totalChunks - 1).toString()
        );
        formData.append(
          'uploadType',
          Constants.UPLOAD_TYPE_RESOURCE
        );

        const chunkResult =
          await httpService.post<{
            tag?: string;
          }>(
            ApiEndpoints.uploadResourceMultipart,
            formData
          );

        if (chunkResult.data?.tag) {
          tags.push(chunkResult.data.tag);
        }

        const percentComplete =
          Math.round(((i + 1) / totalChunks) * 100);

        setUploadProgress(percentComplete);
      }

      const completeUpload = async () => {
        await httpService.post(
          ApiEndpoints.completeResourceMultipartUploadUrl,
          {
            uploadId,
            fileKey,
            tags,
            resourceId,
            uploadType: Constants.UPLOAD_TYPE_RESOURCE,
          }
        );
      };

      await completeUpload();

      if (initiateResult.data.isGuardDutyEnabled) {
        setIsScanning(true);

        const isFileSafe =
          await waitForScanResult(file.name);

        setIsScanning(false);

        if (!isFileSafe) {
          showNotification(
            'error',
            `File ${file.name} failed security scan.`
          );

          setCanUpload(false);
          setUploadProgress(0);

          return;
        }

        showNotification(
          'success',
          'Document uploaded successfully.'
        );

        resetUploadState();

        return;
      }

      showNotification(
        'success',
        'Document uploaded successfully.'
      );

      resetUploadState();
    } catch (error: unknown) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 415) {
        showNotification(
          'error',
          getResponseErrorMessage(
            error,
            'Selected file type is not supported.'
          )
        );
      } else {
        const errorMessage =
          getResponseErrorMessage(
            error,
            'Failed to upload document. Please try again.'
          );

        showNotification(
          'error',
          errorMessage
        );
      }

      setCanUpload(false);
      setIsLoading(false);

      return;
    }

    setIsLoading(false);
  };

  const resetUploadState = () => {
    setSelectedDocumentType('');
    setSelectedFile(null);
    setUploadProgress(0);
    setCanUpload(false);
    setIsLoading(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <Toast
        ref={toast}
        position='top-right'
        appendTo={document.body}
      />

      <p className='admin-section-description'>
        Upload help documents by selecting a document type and choosing a file.
      </p>

      <form
        className='admin-help-grid'
        onSubmit={handleUpload}
      >
        <div className='admin-field'>
          <label htmlFor='documentType'>
            Document Type *
          </label>

          <select
            id='documentType'
            value={selectedDocumentType}
            onChange={(event) => {
              setSelectedDocumentType(
                event.target.value
              );

              setSelectedFile(null);
              setUploadProgress(0);

              if (fileInputRef.current) {
                fileInputRef.current.value =
                  '';
              }
            }}
            disabled={isLoading}
          >
            <option value=''>
              Select document type
            </option>

            {documentTypes.map(
              (option) => (
                <option
                  key={option}
                  value={option}
                >
                  {option}
                </option>
              )
            )}
          </select>
        </div>

        <div className='admin-field'>
          <label htmlFor='helpDocument'>
            Select files to upload *
          </label>

          <input
            id='helpDocument'
            type='file'
            title={fileFormatHint}
            ref={fileInputRef}
            disabled={isLoading}
            onChange={(event) => {
              const file =
                event.target.files?.[0] ??
                null;

              setSelectedFile(file);
              setUploadProgress(0);
            }}
          />
        </div>

        <div className='admin-upload-actions'>
          <button
            className='admin-btn admin-btn-primary'
            type='submit'
            disabled={isLoading}
          >
            {isScanning
              ? 'Scanning...'
              : isLoading
                ? 'Uploading...'
                : 'Upload'}
          </button>
        </div>

        {canUpload && (
          <div className='admin-progress-row'>
            <span>
              {isScanning
                ? 'Scanning file for security threats...'
                : `${uploadProgress}% Uploaded...`}
            </span>

            <ProgressBar
              value={uploadProgress}
              showValue={false}
            />
          </div>
        )}
      </form>
    </>
  );
};

export default HelpDocumentsSection;